import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Personnel } from '@/types/personnel';
import type { UserDetail, Department } from '@shared/types';
import { usersService } from '@/services/users.service';
import { departmentsService } from '@/services/departments.service';
import { handleApiError } from '@/utils/errorHandler';
import { formatDateDisplay } from '@/utils/dateHelpers';
import { fetchImageAsDataUri } from '@/contexts/AvatarContext';

// Types cho kết quả tìm kiếm
export type SearchTab = 'all' | 'timesheets' | 'documents' | 'personnel' | 'departments';

export interface TimesheetResult {
  id: string;
  title: string;
  description: string;
  type: 'timesheet' | 'salary';
}

export interface DocumentResult {
  id: string;
  title: string;
  description: string;
  type: 'leave' | 'other';
}

export interface DepartmentResult {
  id: string;
  name: string;
  code?: string;
  description?: string;
}

export interface SearchResult {
  personnel: Personnel[];
  timesheets: TimesheetResult[];
  documents: DocumentResult[];
  departments: DepartmentResult[];
}

export interface UseHeaderSearchResult {
  // Search query
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  
  // Results
  results: SearchResult;
  loading: boolean;
  error: string | null;
  
  // Active tab
  activeTab: SearchTab;
  setActiveTab: (tab: SearchTab) => void;
  
  // Filtered results theo tab
  filteredResults: SearchResult;
  counts: {
    all: number;
    personnel: number;
    timesheets: number;
    documents: number;
    departments: number;
  };
  
  // Avatar cache
  avatarCacheRef: React.MutableRefObject<Map<string, string>>;
  fetchAvatarAsDataUri: (url: string) => Promise<string | null>;
  
  // Actions
  handlePersonnelClick: (personnel: Personnel) => void;
  handleDepartmentClick: () => void;
  handleDocumentClick: (document: { id: string; type: string; title?: string; description?: string }) => void;
}

// Constants
const MONTH_NAMES = [
  'tháng 1', 'tháng 2', 'tháng 3', 'tháng 4', 'tháng 5', 'tháng 6',
  'tháng 7', 'tháng 8', 'tháng 9', 'tháng 10', 'tháng 11', 'tháng 12'
];

// Helper functions - Parse query để tìm tháng/năm
const getCurrentDateInfo = () => {
  const now = new Date();
  return {
    year: now.getFullYear(),
    month: now.getMonth() + 1,
  };
};

// Hàm hỗ trợ phân tích tháng và năm từ văn bản
// Hỗ trợ các định dạng như: "tháng 12", "tháng 12/2025", "tháng 9/2025", "Bảng công 2025"
// Chỉ trả về các năm hiện tại hoặc quá khứ (không phải tương lai)
const parseMonthYear = (text: string): { month: number | null; year: number | null } => {
  const lowerText = text.toLowerCase();
  const currentYear = new Date().getFullYear();
  
  // Mẫu 1: "tháng 12/2025" hoặc "tháng 12 / 2025"
  const pattern1 = /tháng\s+(\d+)\s*\/\s*(\d+)/;
  const match1 = lowerText.match(pattern1);
  if (match1) {
    const month = parseInt(match1[1], 10);
    const year = parseInt(match1[2], 10);
    // Chỉ chấp nhận năm hiện tại hoặc các năm trước đó
    if (month >= 1 && month <= 12 && year >= 2000 && year <= currentYear) {
      return { month, year };
    }
  }
  
  // Mẫu 2: "tháng 12" (chỉ có tháng, sử dụng năm hiện tại cho gợi ý)
  const pattern2 = /tháng\s+(\d+)/;
  const match2 = lowerText.match(pattern2);
  if (match2) {
    const month = parseInt(match2[1], 10);
    if (month >= 1 && month <= 12) {
      // Trả về năm hiện tại cho gợi ý khi chỉ có tháng được chỉ định
      return { month, year: currentYear };
    }
  }
  
  return { month: null, year: null };
};

// Hàm hỗ trợ phân tích năm từ định dạng "Bảng công 2025"
// Cũng hỗ trợ năm một phần như "Bảng công 2" hoặc "Bảng công 202"
const parseYearFromTimesheetQuery = (text: string): { year: number | null; isPartial: boolean; yearPrefix: string | null } => {
  const lowerText = text.toLowerCase();
  const currentYear = new Date().getFullYear();
  
  // Mẫu 1: Năm đầy đủ "bảng công 2025" hoặc "bảng công 2024"
  const pattern1 = /bảng\s+công\s+(\d{4})/;
  const match1 = lowerText.match(pattern1);
  if (match1) {
    const year = parseInt(match1[1], 10);
    // Chỉ chấp nhận năm hiện tại hoặc các năm trước đó
    if (year >= 2000 && year <= currentYear) {
      return { year, isPartial: false, yearPrefix: null };
    }
  }
  
  // Mẫu 2: Năm một phần "bảng công 2" hoặc "bảng công 202"
  const pattern2 = /bảng\s+công\s+(\d{1,3})$/;
  const match2 = lowerText.match(pattern2);
  if (match2) {
    const yearPrefix = match2[1];
    return { year: null, isPartial: true, yearPrefix };
  }
  
  return { year: null, isPartial: false, yearPrefix: null };
};

// Hàm hỗ trợ tạo gợi ý năm dựa trên thông tin năm được nhập một phần
const generateYearSuggestions = (yearPrefix: string, maxResults: number = 10): number[] => {
  const currentYear = new Date().getFullYear();
  const suggestions: number[] = [];
  
  // Tạo các năm bắt đầu bằng tiền tố
  // Bắt đầu từ năm hiện tại và lùi về trước
  for (let year = currentYear; year >= 2000 && suggestions.length < maxResults; year--) {
    if (year.toString().startsWith(yearPrefix)) {
      suggestions.push(year);
    }
  }
  
  return suggestions;
};

// Hàm hỗ trợ để tạo kết quả bảng chấm công
const createTimesheetResult = (
  id: string,
  month: number | null,
  year: number | null,
  type: 'timesheet' | 'salary' = 'timesheet'
): TimesheetResult => {
  if (month !== null) {
    return {
      id,
      title: `Bảng công ${MONTH_NAMES[month - 1]}`,
      description: year ? `Chi tiết bảng lương ${MONTH_NAMES[month - 1]}/${year}` : `Chi tiết bảng lương ${MONTH_NAMES[month - 1]}`,
      type,
    };
  }
  return {
    id,
    title: `Bảng ${type === 'salary' ? 'lương' : 'công'} ${year || ''}`,
    description: `Xem bảng ${type === 'salary' ? 'lương' : 'công'} năm ${year || ''}`,
    type,
  };
};

// Hàm hỗ trợ tạo chứng từ bảng chấm công cho một tháng cụ thể
const generateTimesheetDocuments = (month: number, year?: number | null): TimesheetResult[] => {
  const { year: currentYear, month: currentMonth } = getCurrentDateInfo();
  const documents: TimesheetResult[] = [];
  
  if (year) {
    const isValidMonth = (year < currentYear) || (year === currentYear && month <= currentMonth);
    if (isValidMonth && month >= 1 && month <= 12) {
      documents.push(createTimesheetResult(`timesheet-${year}-${month}`, month, year));
    }
    return documents;
  }
  
  const maxYearsBack = 10;
  const startYear = Math.max(currentYear - maxYearsBack, 2000);
  
  for (let targetYear = currentYear; targetYear >= startYear; targetYear--) {
    const maxMonth = (targetYear === currentYear) ? currentMonth : 12;
    if (month >= 1 && month <= 12 && month <= maxMonth) {
      documents.push(createTimesheetResult(`timesheet-${targetYear}-${month}`, month, targetYear));
    }
  }
  
  return documents;
};

// Hàm hỗ trợ tìm kiếm tài liệu bảng chấm công dựa trên truy vấn
const searchTimesheetDocuments = (query: string): TimesheetResult[] => {
  const { year: currentYear, month: currentMonth } = getCurrentDateInfo();
  const queryLower = query.toLowerCase().trim();
  const queryMonthYear = parseMonthYear(query);
  const queryYearInfo = parseYearFromTimesheetQuery(query);
  const results: TimesheetResult[] = [];
  const startsWithBangCong = queryLower.startsWith('bảng công') || queryLower.startsWith('bang cong');

  if (startsWithBangCong) {
    // Trường hợp 1: Nhập năm một phần
    if (queryYearInfo.isPartial && queryYearInfo.yearPrefix) {
      const yearSuggestions = generateYearSuggestions(queryYearInfo.yearPrefix, 10);
      yearSuggestions.forEach((year) => {
        results.push(createTimesheetResult(`year-suggestion-${year}`, null, year));
      });
      return results;
    }
    
    // Trường hợp 2: Chỉ định năm đầy đủ
    if (queryYearInfo.year && !queryMonthYear.month) {
      const maxMonth = (queryYearInfo.year === currentYear) ? currentMonth : 12;
      for (let month = 1; month <= maxMonth; month++) {
        results.push(createTimesheetResult(`timesheet-${queryYearInfo.year}-${month}`, month, queryYearInfo.year));
      }
      return results;
    }
    
    // Trường hợp 3: "Bảng công tháng" không có tháng/năm cụ thể
    if (queryLower.includes('bảng công tháng') && !/\d+/.test(queryLower.replace(/bảng công tháng/g, '').trim())) {
      for (let month = 1; month <= currentMonth; month++) {
        results.push(createTimesheetResult(`timesheet-${currentYear}-${month}`, month, currentYear));
      }
      return results;
    }
    
    // Trường hợp 4: Chỉ "Bảng công"
    if (queryLower === 'bảng công' || queryLower === 'bang cong' || queryLower.match(/^bảng\s+công\s*$/)) {
      results.push(createTimesheetResult(`year-suggestion-${currentYear}`, null, currentYear));
      return results;
    }
  }
  
  // Trường hợp 5: Truy vấn tháng cụ thể
  if (queryMonthYear.month) {
    return generateTimesheetDocuments(queryMonthYear.month, queryMonthYear.year);
  }
  
  // Trường hợp 6: Tìm kiếm chung với từ khóa
  if (queryLower.includes('bảng') || queryLower.includes('công') || queryLower.includes('tháng')) {
    results.push(createTimesheetResult(`year-suggestion-${currentYear}`, null, currentYear));
    return results;
  }
  
  // Trường hợp 7: Truy vấn bắt đầu bằng "B" (nhập ngắn)
  if (queryLower.startsWith('b') && query.length <= 3) {
    results.push(createTimesheetResult(`year-suggestion-${currentYear}`, null, currentYear));
    results.push(createTimesheetResult(`salary-suggestion-${currentYear}`, null, currentYear, 'salary'));
    return results;
  }
  
  return results;
};

// Transform user thành personnel
const transformUserToPersonnel = (user: UserDetail): Personnel => ({
  id: user.id.toString(),
  employeeCode: user.employeeCode || `NV${user.id}`,
  fullName: user.fullName,
  position: user.role || 'staff',
  company: '',
  jobTitle: user.position || '',
  status: (user.isBlocked ? 'locked' : (user.isActive ? 'active' : 'locked')) as 'active' | 'locked',
  startDate: user.startDate ? formatDateDisplay(user.startDate) : formatDateDisplay(new Date()),
  avatar: user.avatar,
  role: user.role,
  email: user.email,
  departmentId: user.departmentId,
});

// Lọc personnel theo truy vấn
const matchesPersonnel = (person: Personnel, queryLower: string): boolean => {
  return (
    person.fullName.toLowerCase().includes(queryLower) ||
    (person.email?.toLowerCase().includes(queryLower) ?? false) ||
    person.employeeCode.toLowerCase().includes(queryLower)
  );
};

// Chuyển đổi department thành kết quả
const transformDepartment = (dept: Department): DepartmentResult => ({
  id: dept.id.toString(),
  name: dept.name,
  code: dept.code,
  description: `Phòng ban ${dept.name}`,
});

// Lọc department theo truy vấn
const matchesDepartment = (dept: DepartmentResult, queryLower: string): boolean => {
  return (
    dept.name.toLowerCase().includes(queryLower) ||
    (dept.code?.toLowerCase().includes(queryLower) ?? false)
  );
};

export const useHeaderSearch = (initialSearchQuery?: string): UseHeaderSearchResult => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery || '');
  const [activeTab, setActiveTab] = useState<SearchTab>('all');
  
  // Sync searchQuery với prop nếu có
  useEffect(() => {
    if (initialSearchQuery !== undefined) {
      setSearchQuery(initialSearchQuery);
    }
  }, [initialSearchQuery]);
  const [results, setResults] = useState<SearchResult>({
    personnel: [],
    timesheets: [],
    documents: [],
    departments: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const avatarCacheRef = useRef<Map<string, string>>(new Map());

  // Helper function để fetch avatar với cache
  // imageUrl đã được buildFullUrl() trước đó, nên chỉ cần fetch trực tiếp
  const fetchAvatarAsDataUri = useCallback(async (imageUrl: string): Promise<string | null> => {
    // Check cache first
    if (avatarCacheRef.current.has(imageUrl)) {
      return avatarCacheRef.current.get(imageUrl) || null;
    }

    // Fetch avatar từ URL (đã là full URL) và convert sang data URI
    try {
      const dataUri = await fetchImageAsDataUri(imageUrl);
      
      // Cache the result
      if (dataUri) {
        avatarCacheRef.current.set(imageUrl, dataUri);
      }
      
      return dataUri;
    } catch (err) {
      console.error('Error loading avatar:', err);
      return null;
    }
  }, []);

  // Hàm tìm kiếm - gọi API song song để tăng tốc độ
  const performSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setResults({ personnel: [], timesheets: [], documents: [], departments: [] });
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    
    let cancelled = false; // Flag để cancel nếu component unmount hoặc query thay đổi

    try {
      const queryLower = query.toLowerCase().trim();
      
      // Gọi 2 API song song thay vì tuần tự → giảm thời gian chờ
      const [personnelResponse, departmentsResponse] = await Promise.all([
        usersService.getUsers({ search: query, limit: 10 }),
        departmentsService.getDepartments({ search: query, limit: 10 }),
      ]);

      // Chỉ update state nếu component vẫn còn mount và query chưa thay đổi
      if (!cancelled) {
        const personnelResults: Personnel[] = personnelResponse.data
          ? personnelResponse.data
              .map((user: UserDetail) => transformUserToPersonnel(user))
              .filter((person) => matchesPersonnel(person, queryLower))
          : [];

        const departmentResults: DepartmentResult[] = departmentsResponse.data
          ? departmentsResponse.data
              .filter((dept: Department) => dept.isActive)
              .map(transformDepartment)
              .filter((dept) => matchesDepartment(dept, queryLower))
          : [];

        const timesheetResults = searchTimesheetDocuments(query);

        setResults({
          personnel: personnelResults,
          timesheets: timesheetResults,
          documents: [],
          departments: departmentResults,
        });
        setError(null); // Clear error on success
      }
    } catch (err: any) {
      // Chỉ xử lý lỗi nếu component vẫn còn mount và query chưa thay đổi
      if (!cancelled) {
        handleApiError(err, 'Không thể tải kết quả tìm kiếm');
        const errorMessage = err?.message || 'Không thể tải kết quả tìm kiếm';
        setError(errorMessage);
        setResults({ personnel: [], timesheets: [], documents: [], departments: [] });
      }
    } finally {
      if (!cancelled) {
        setLoading(false);
      }
    }
  }, []);

  // Tìm kiếm với debounce 300ms để tránh gọi API quá nhiều khi user gõ
  useEffect(() => {
    if (!searchQuery.trim()) {
      setResults({ personnel: [], timesheets: [], documents: [], departments: [] });
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false; // Flag để cancel request nếu query thay đổi trước khi debounce timeout

    const timeoutId = setTimeout(() => {
      if (!cancelled) {
        performSearch(searchQuery);
      }
    }, 300);

    return () => {
      cancelled = true; // Cancel request nếu query thay đổi
      clearTimeout(timeoutId);
    };
  }, [searchQuery, performSearch]);

  // Tính toán filtered results theo tab hiện tại
  const filteredResults = useMemo(() => {
    const isAllTab = activeTab === 'all';
    return {
      personnel: (isAllTab || activeTab === 'personnel') ? results.personnel : [],
      timesheets: (isAllTab || activeTab === 'timesheets') ? results.timesheets : [],
      documents: (isAllTab || activeTab === 'documents') ? results.documents : [],
      departments: (isAllTab || activeTab === 'departments') ? results.departments : [],
    };
  }, [results, activeTab]);

  // Tính toán counts cho từng tab
  const counts = useMemo(() => ({
    all: results.personnel.length + results.timesheets.length + results.documents.length + results.departments.length,
    personnel: results.personnel.length,
    timesheets: results.timesheets.length,
    documents: results.documents.length,
    departments: results.departments.length,
  }), [results]);

  // Actions - Navigation handlers
  const handlePersonnelClick = useCallback((personnel: Personnel) => {
    navigate(`/management/personnel/detail/${personnel.employeeCode}`);
  }, [navigate]);

  const handleDepartmentClick = useCallback(() => {
    navigate(`/management/department`);
  }, [navigate]);

  const handleDocumentClick = useCallback((document: { id: string; type: string; title?: string; description?: string }) => {
    if (document.type === 'timesheet' || document.type === 'salary') {
      if (document.id?.startsWith('year-suggestion-')) {
        const year = parseInt(document.id.replace('year-suggestion-', ''), 10);
        if (year) {
          navigate(`/management/salary/timesheet-table?year=${year}`);
          return;
        }
      }
      
      // Parse month/year từ title hoặc description
      const descResult = document.description ? parseMonthYear(document.description) : { month: null, year: null };
      const titleResult = document.title ? parseMonthYear(document.title) : { month: null, year: null };
      const month = descResult.month || titleResult.month;
      const year = descResult.year || titleResult.year;
      
      const params = new URLSearchParams();
      if (month) params.set('month', month.toString());
      if (year) params.set('year', year.toString());
      
      const url = params.toString() 
        ? `/management/salary/timesheet-table?${params.toString()}`
        : '/management/salary/timesheet-table';
      
      navigate(url);
    }
  }, [navigate]);

  return {
    searchQuery,
    setSearchQuery,
    results,
    loading,
    error,
    activeTab,
    setActiveTab,
    filteredResults,
    counts,
    avatarCacheRef,
    fetchAvatarAsDataUri,
    handlePersonnelClick,
    handleDepartmentClick,
    handleDocumentClick,
  };
};

